import { Fragment, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { ChevronDown, PackageCheck, Search, X, Check, ChevronsUpDown, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { mediaUrl } from "./ProductsPage";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { unitLabel, fmtDate } from "../lib/format";

const pkey = (p) => `${p.sku}|${p.variant || ""}`;

export default function MalKabulPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("");
  const [prod, setProd] = useState("");
  const [prodOpen, setProdOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState({});
  const [editEntry, setEditEntry] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const canUse = user?.role === "admin" || user?.role === "malkabul";

  const load = () => {
    api.get("/orders/by-product")
      .then((r) => {
        const flat = [];
        r.data.data.forEach((g) => g.products.forEach((p) => flat.push(p)));
        setProducts(flat);
      })
      .catch(() => toast.error("Ürünler yüklenemedi"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (canUse) load(); }, [canUse]);

  const categories = useMemo(() => [...new Set(products.map((p) => p.category))].sort((a, b) => a.localeCompare(b, "tr")), [products]);
  const catProducts = useMemo(() => products.filter((p) => !cat || p.category === cat), [products, cat]);
  const selectedProd = products.find((p) => pkey(p) === prod);

  const stats = useMemo(() => {
    const s = { total: products.length, tam: 0, eksik: 0, fazla: 0, bekleyen: 0, orderQty: 0, procuredQty: 0, acceptedQty: 0 };
    products.forEach((p) => {
      const a = p.acceptedQty || 0;
      if (a === 0) s.bekleyen += 1;
      else if (a === p.qty) s.tam += 1;
      else if (a < p.qty) s.eksik += 1;
      else s.fazla += 1;
      s.orderQty += p.qty || 0;
      s.procuredQty += p.procuredQty || 0;
      s.acceptedQty += a;
    });
    return s;
  }, [products]);

  const rows = useMemo(() => {
    let v = products;
    if (filter === "bekleyen") v = v.filter((p) => !(p.acceptedQty || 0));
    else {
      v = v.filter((p) => (p.acceptedQty || 0) > 0);
      if (filter === "tam") v = v.filter((p) => p.acceptedQty === p.qty);
      if (filter === "eksik") v = v.filter((p) => p.acceptedQty < p.qty);
      if (filter === "fazla") v = v.filter((p) => p.acceptedQty > p.qty);
    }
    const q = search.trim().toLowerCase();
    if (q) v = v.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q));
    return [...v].sort((a, b) => (a.category || "").localeCompare(b.category || "", "tr") || (a.name || "").localeCompare(b.name || "", "tr"));
  }, [products, filter, search]);

  const save = async () => {
    if (!selectedProd) return;
    setSaving(true);
    try {
      await api.post("/procurement/receive", { sku: selectedProd.sku, variant: selectedProd.variant || null, qty: Number(qty), date });
      toast.success(`${selectedProd.name}: ${qty} ${unitLabel(selectedProd.unit)} mal kabul kaydedildi`);
      setQty("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (d) => {
    try {
      await api.delete(`/procurement/delivery/${d.id}`);
      toast.success("Kayıt silindi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  const saveEdit = async () => {
    if (!editEntry || !editEntry.qty || Number(editEntry.qty) <= 0) return;
    setEditSaving(true);
    try {
      await api.post("/procurement/delivery-update", { id: editEntry.id, qty: Number(editEntry.qty), date: editEntry.date });
      toast.success("Kayıt güncellendi");
      setEditEntry(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Güncellenemedi");
    } finally {
      setEditSaving(false);
    }
  };

  if (user && !canUse) return <Navigate to="/" replace />;

  const KPIS = [
    ["all", "Sipariş Edilen Ürün", stats.total, "border-stone-300", "text-stone-600"],
    ["tam", "Tam ✓", stats.tam, "border-green-400 bg-green-50/50", "text-green-700"],
    ["eksik", "Eksik", stats.eksik, "border-red-300 bg-red-50/50", "text-red-700"],
    ["fazla", "Fazla", stats.fazla, "border-amber-300 bg-amber-50/50", "text-amber-700"],
    ["bekleyen", "Girilmemiş", stats.bekleyen, "border-stone-300 bg-stone-50", "text-stone-500"],
  ];

  return (
    <main className="mx-auto max-w-7xl space-y-4 px-4 py-6 md:px-6" data-testid="malkabul-page">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="malkabul-kpi-cards">
        {KPIS.map(([val, label, count, cls, txt]) => (
          <button
            key={val}
            type="button"
            onClick={() => setFilter((cur) => (cur === val ? "all" : val))}
            data-testid={`malkabul-kpi-${val}`}
            className={`border p-3 text-left transition-all active:scale-[0.98] ${cls} ${filter === val ? "ring-2 ring-[#1C4E35] ring-offset-1" : "hover:opacity-80"}`}
          >
            <p className={`text-[10px] font-bold uppercase tracking-[0.15em] ${txt}`}>{label}</p>
            <p className="mt-1 font-heading text-2xl font-black tracking-tight text-[#0A0D0B]">{loading ? "…" : count}</p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border border-stone-200 bg-white px-4 py-2.5" data-testid="malkabul-qty-totals">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400">Adet Toplamları</span>
        <span className="text-xs text-stone-600">Sipariş: <strong className="font-heading text-sm text-[#0A0D0B]" data-testid="malkabul-total-order-qty">{loading ? "…" : stats.orderQty}</strong></span>
        <span className="text-xs text-stone-600">Satınalma: <strong className="font-heading text-sm text-blue-700" data-testid="malkabul-total-procured-qty">{loading ? "…" : stats.procuredQty}</strong></span>
        <span className="text-xs text-stone-600">Mal Kabul: <strong className="font-heading text-sm text-green-700" data-testid="malkabul-total-accepted-qty">{loading ? "…" : stats.acceptedQty}</strong></span>
        {!loading && (
          <span className="text-xs text-stone-600">
            Sipariş − Mal Kabul: <strong className={`font-heading text-sm ${stats.acceptedQty - stats.orderQty === 0 ? "text-green-700" : stats.acceptedQty - stats.orderQty < 0 ? "text-red-700" : "text-amber-700"}`} data-testid="malkabul-total-diff">{stats.acceptedQty - stats.orderQty > 0 ? `+${stats.acceptedQty - stats.orderQty}` : stats.acceptedQty - stats.orderQty}</strong>
          </span>
        )}
      </div>

      <div className="border border-[#1C4E35]/30 bg-[#1C4E35]/5 p-4" data-testid="malkabul-entry-card">
        <p className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#1C4E35]">
          <PackageCheck size={14} /> Yeni Mal Kabul Girişi
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <p className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">Grup / Kategori</p>
            <Select value={cat || "__all__"} onValueChange={(v) => { setCat(v === "__all__" ? "" : v); setProd(""); }}>
              <SelectTrigger data-testid="malkabul-cat-select" className="h-9 rounded-sm border-stone-300 bg-white text-xs">
                <SelectValue placeholder="Kategori seçin" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__all__">Tüm Kategoriler</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-96 min-w-0">
            <p className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">Ürün</p>
            <Popover open={prodOpen} onOpenChange={setProdOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="malkabul-product-select"
                  className="flex h-9 w-full items-center justify-between rounded-sm border border-stone-300 bg-white px-3 text-xs"
                >
                  <span className={`truncate ${selectedProd ? "text-stone-900" : "text-stone-400"}`}>
                    {selectedProd ? `${selectedProd.name}${selectedProd.variant ? ` (${selectedProd.variant})` : ""}` : "Ürün seçin veya arayın..."}
                  </span>
                  <ChevronsUpDown size={13} className="shrink-0 text-stone-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[420px] p-0" align="start">
                <Command filter={(value, s) => (value.toLowerCase().includes(s.toLowerCase()) ? 1 : 0)}>
                  <CommandInput placeholder="Ürün adı veya SKU yazın..." data-testid="malkabul-product-search" className="text-xs" />
                  <CommandList className="max-h-72">
                    <CommandEmpty>Ürün bulunamadı.</CommandEmpty>
                    <CommandGroup>
                      {catProducts.map((p) => {
                        const k = pkey(p);
                        return (
                          <CommandItem
                            key={k}
                            value={`${p.name} ${p.variant || ""} ${p.sku}`}
                            onSelect={() => { setProd(k); setProdOpen(false); }}
                            data-testid={`malkabul-product-option-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                            className="text-xs"
                          >
                            <Check size={12} className={`mr-1.5 shrink-0 ${prod === k ? "opacity-100 text-[#1C4E35]" : "opacity-0"}`} />
                            {p.image ? (
                              <img src={mediaUrl(p.image)} alt={p.name} loading="lazy" className="mr-2 h-8 w-8 shrink-0 border border-stone-200 bg-white object-contain" />
                            ) : (
                              <span className="mr-2 h-8 w-8 shrink-0 border border-stone-100 bg-stone-50" />
                            )}
                            <span className="min-w-0 flex-1 truncate">{p.name}{p.variant ? ` (${p.variant})` : ""}</span>
                            <span className="ml-2 shrink-0 text-[10px] text-stone-400">Sipariş {p.qty} · SA {p.procuredQty || 0} · MK {p.acceptedQty || 0}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="w-28">
            <p className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">Miktar</p>
            <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} placeholder={selectedProd ? `kalan ${Math.max(0, selectedProd.qty - (selectedProd.acceptedQty || 0))}` : "adet"} data-testid="malkabul-qty-input" className="h-9 rounded-sm border-stone-300 bg-white text-xs" />
          </div>
          <div className="w-40">
            <p className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">Tarih</p>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="malkabul-date-input" className="h-9 rounded-sm border-stone-300 bg-white text-xs" />
          </div>
          <Button
            onClick={save}
            disabled={!selectedProd || !qty || Number(qty) <= 0 || saving}
            data-testid="malkabul-save-button"
            className="h-9 rounded-sm bg-[#1C4E35] px-5 text-xs text-white hover:bg-[#143A26] active:scale-95"
          >
            {saving ? "Kaydediliyor..." : "Mal Kabul Kaydet"}
          </Button>
          {selectedProd && (
            <span className="text-[11px] text-stone-500" data-testid="malkabul-selected-info">
              Sipariş: <strong>{selectedProd.qty}</strong> · Satınalma: <strong className="text-blue-700">{selectedProd.procuredQty || 0}</strong> · Mal Kabul: <strong className="text-green-700">{selectedProd.acceptedQty || 0}</strong>
              {Number(qty) > 0 && Number(qty) + (selectedProd.acceptedQty || 0) > selectedProd.qty && (
                <span className="ml-1 border border-amber-300 bg-amber-50 px-1 py-px text-[10px] font-medium text-amber-700">Fazla girilecek</span>
              )}
            </span>
          )}
        </div>
      </div>

      <div className="border border-stone-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-stone-200 p-3">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ürün adı veya SKU ara..." data-testid="malkabul-search" className="h-8 w-64 rounded-sm border-stone-300 pl-8 text-xs" />
          </div>
          <p className="ml-auto text-xs text-stone-500">{rows.length} ürün</p>
        </div>
        {loading ? (
          <div className="flex h-32 items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-[#1C4E35] border-t-transparent" /></div>
        ) : (
          <Table data-testid="malkabul-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Ürün</TableHead>
                <TableHead className="text-xs">Kategori</TableHead>
                <TableHead className="text-right text-xs">Sipariş</TableHead>
                <TableHead className="text-right text-xs">Satınalma</TableHead>
                <TableHead className="text-right text-xs">Mal Kabul</TableHead>
                <TableHead className="text-right text-xs">Sipariş Farkı</TableHead>
                <TableHead className="text-right text-xs">Satınalma Farkı</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((p) => {
                const k = pkey(p);
                const a = p.acceptedQty || 0;
                const pr = p.procuredQty || 0;
                const diff = a - p.qty;
                const prDiff = a - pr;
                const entries = (p.deliveryEntries || []).filter((d) => d.status !== "pending");
                const isOpen = !!open[k];
                return (
                  <Fragment key={k}>
                    <TableRow onClick={() => entries.length && setOpen((o) => ({ ...o, [k]: !o[k] }))} className={`${entries.length ? "cursor-pointer" : ""} hover:bg-stone-50`} data-testid={`malkabul-row-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}>
                      <TableCell className="py-2">
                        <span className="flex items-center gap-2">
                          {entries.length > 0 && <ChevronDown size={12} className={`shrink-0 text-stone-400 transition-transform ${isOpen ? "" : "-rotate-90"}`} />}
                          {p.image && <img src={mediaUrl(p.image)} alt={p.name} loading="lazy" className="h-9 w-9 shrink-0 border border-stone-200 bg-white object-contain" />}
                          <span>
                            <p className="text-sm font-medium">{p.name}{p.variant ? <span className="ml-1 text-xs text-stone-500">({p.variant})</span> : null}{(p.manualQty || 0) > 0 ? <span className="ml-1.5 border border-violet-300 bg-violet-50 px-1 py-px text-[9px] font-bold text-violet-700" title={`Manuel ekleme içerir (${p.manualQty} adet)`}>M</span> : null}</p>
                            <p className="text-[10px] text-stone-400">{p.sku}</p>
                          </span>
                        </span>
                      </TableCell>
                      <TableCell className="py-2 text-xs text-stone-600">{p.category}</TableCell>
                      <TableCell className="py-2 text-right text-sm font-medium">{p.qty}</TableCell>
                      <TableCell className={`py-2 text-right text-sm font-medium ${pr > 0 ? "text-blue-700" : "text-stone-400"}`} data-testid={`malkabul-procured-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}>{pr}</TableCell>
                      <TableCell className={`py-2 text-right text-sm font-medium ${a > 0 ? "text-green-700" : "text-stone-400"}`}>{a}</TableCell>
                      <TableCell className="py-2 text-right">
                        {a === 0 ? (
                          <span className="text-[11px] text-stone-400">—</span>
                        ) : (
                          <span className={`border px-1.5 py-0.5 text-[10px] font-medium ${diff === 0 ? "border-green-300 bg-green-50 text-green-700" : diff < 0 ? "border-red-300 bg-red-50 text-red-700" : "border-amber-300 bg-amber-50 text-amber-700"}`} data-testid={`malkabul-diff-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}>
                            {diff === 0 ? "Tam ✓" : diff < 0 ? `Eksik ${-diff}` : `Fazla +${diff}`}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        {pr === 0 && a === 0 ? (
                          <span className="text-[11px] text-stone-400">—</span>
                        ) : (
                          <span className={`border px-1.5 py-0.5 text-[10px] font-medium ${prDiff === 0 ? "border-green-300 bg-green-50 text-green-700" : prDiff < 0 ? "border-red-300 bg-red-50 text-red-700" : "border-amber-300 bg-amber-50 text-amber-700"}`} data-testid={`malkabul-procdiff-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}>
                            {prDiff === 0 ? "Tam ✓" : prDiff < 0 ? `Eksik ${-prDiff}` : `Fazla +${prDiff}`}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                    {isOpen && entries.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-stone-50/70 py-2">
                          <div className="space-y-1 px-8">
                            {entries.map((d, i) => (
                              editEntry && editEntry.id === d.id ? (
                                <div key={d.id || i} className="flex flex-wrap items-center gap-2" data-testid={`malkabul-entry-edit-${i}`}>
                                  <Input type="number" min="1" value={editEntry.qty} onChange={(e) => setEditEntry((v) => ({ ...v, qty: e.target.value }))} data-testid="malkabul-entry-edit-qty" className="h-7 w-20 rounded-sm border-stone-300 bg-white text-xs" />
                                  <Input type="date" value={editEntry.date} onChange={(e) => setEditEntry((v) => ({ ...v, date: e.target.value }))} data-testid="malkabul-entry-edit-date" className="h-7 w-36 rounded-sm border-stone-300 bg-white text-xs" />
                                  <button type="button" onClick={(e) => { e.stopPropagation(); saveEdit(); }} disabled={editSaving} data-testid="malkabul-entry-edit-save" className="border border-[#1C4E35] bg-[#1C4E35] px-2 py-1 text-[10px] font-medium text-white hover:bg-[#143A26]">
                                    {editSaving ? "..." : "Kaydet"}
                                  </button>
                                  <button type="button" onClick={(e) => { e.stopPropagation(); setEditEntry(null); }} data-testid="malkabul-entry-edit-cancel" className="border border-stone-300 bg-white px-2 py-1 text-[10px] text-stone-600 hover:bg-stone-100">
                                    Vazgeç
                                  </button>
                                </div>
                              ) : (
                              <div key={d.id || i} className="flex flex-wrap items-center gap-2 text-[11px] text-stone-600">
                                <span className="font-medium text-green-700">{d.qty} {unitLabel(p.unit)}</span>
                                <span className="text-stone-400">{fmtDate(d.date)}</span>
                                {d.createdAt && (
                                  <span className="text-stone-400" data-testid={`malkabul-entry-time-${i}`}>
                                    Giriş: {new Date(d.createdAt).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                  </span>
                                )}
                                {d.enteredBy && <span className="border border-stone-300 bg-white px-1 py-px text-[9px]" data-testid={`malkabul-entry-by-${i}`}>{d.enteredBy}</span>}
                                {d.supplier && d.supplier !== d.enteredBy && <span className="border border-stone-300 bg-white px-1 py-px text-[9px]">{d.supplier}</span>}
                                {d.id && (
                                  <>
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setEditEntry({ id: d.id, qty: String(d.qty), date: (d.date || "").slice(0, 10) }); }} title="Kaydı düzenle" data-testid={`malkabul-entry-edit-btn-${i}`} className="text-stone-400 transition-colors hover:text-[#1C4E35]">
                                      <Pencil size={12} />
                                    </button>
                                    <button type="button" onClick={(e) => { e.stopPropagation(); removeEntry(d); }} title="Kaydı sil" data-testid={`malkabul-entry-delete-${i}`} className="text-stone-400 transition-colors hover:text-red-600">
                                      <X size={12} />
                                    </button>
                                  </>
                                )}
                              </div>
                            )))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </main>
  );
}
